import { useState } from "react";
import axios from "axios";
import { Search, Loader2, Check, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Compact button that takes an org number and calls /api/admin/company-lookup/:orgnr
 * Calls onResult(data) with the normalized company info to let the parent auto-fill its form.
 *
 * Props:
 *  - orgnr: current value (string)
 *  - onResult: function(data) called with normalized company info
 *  - label: optional button label override (default "Lookup")
 *  - compact: bool — render as icon-only button
 */
export default function OrgLookupButton({ orgnr, onResult, label = "Lookup", compact = false }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const cleaned = (orgnr || "").replace(/\D/g, "");
  const valid = cleaned.length === 10 || cleaned.length === 12;

  const lookup = async () => {
    if (!valid || loading) return;
    setLoading(true); setError(""); setSuccess(false);
    try {
      const r = await axios.get(`${API}/admin/company-lookup/${cleaned}`, { withCredentials: true });
      onResult(r.data);
      setSuccess(true);
      setTimeout(() => setSuccess(false), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || "Lookup failed");
      setTimeout(() => setError(""), 3000);
    }
    setLoading(false);
  };

  if (compact) {
    return (
      <button type="button" onClick={lookup} disabled={!valid || loading}
        title={error || (valid ? "Look up company info" : "Enter 10-digit org number")}
        data-testid="org-lookup-btn"
        className={`p-2 rounded-sm transition-colors ${success ? "text-green-400" : error ? "text-red-400" : valid ? "text-[#F59073] hover:text-[#F59073] hover:bg-[#F59073]/10" : "text-[#857973]/40 cursor-not-allowed"}`}>
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : success ? <Check className="w-4 h-4" /> : error ? <AlertCircle className="w-4 h-4" /> : <Search className="w-4 h-4" />}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <button type="button" onClick={lookup} disabled={!valid || loading}
        data-testid="org-lookup-btn"
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-sm text-xs font-medium transition-all ${success ? "bg-green-500/15 text-green-400 border border-green-500/30" : error ? "bg-red-500/15 text-red-400 border border-red-500/30" : valid ? "bg-[#F59073] text-black hover:shadow-[0_0_15px_rgba(245,144,115,0.3)]" : "bg-[#1E3050] text-[#857973]/50 cursor-not-allowed"}`}>
        {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : success ? <Check className="w-3.5 h-3.5" /> : <Search className="w-3.5 h-3.5" />}
        {loading ? "Looking up…" : success ? "Filled" : label}
      </button>
      {error && <span className="text-xs text-red-400 truncate max-w-[200px]" title={error}>{error}</span>}
    </div>
  );
}
